import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import useGlynt from './useGlynt';

export default function usePaginatedDataPools(
  organization: string | null,
  limit: number = 100,
  ordering: string | undefined = 'label',
  enabled: boolean = true
) {
  const [page, setPage] = useState(0);
  const glynt = useGlynt();

  useEffect(() => {
    setPage(0);
  }, [organization]);

  const { data, isError, isPending, isFetching } = useQuery({
    queryKey: ['datapools', { organization, page, limit, ordering }],
    queryFn: async () =>
      (await glynt).dataPool.list({
        organization,
        advanced: true,
        limit,
        offset: page * limit,
        ordering: ordering,
      }),
    enabled,
    refetchOnWindowFocus: false,
    placeholderData: (previousData, previousQuery) => {
      const previousParams = previousQuery?.queryKey?.[1] as
        | { organization?: string | null }
        | undefined;

      return previousParams?.organization === organization ? keepPreviousData(previousData) : undefined;
    },
  });

  const total = data ? Math.ceil(data.count / limit) : 1;

  const nextPage = () => {
    if (page < total - 1) setPage((page) => page + 1);
  };

  const previousPage = () => {
    if (page > 0) setPage((page) => page - 1);
  };

  const toPage = (goToPage: number) => {
    if (goToPage >= 0 && goToPage <= total) {
      setPage((page) => goToPage - 1);
    }
  };

  return {
    data,
    isError,
    isPending,
    isLoading: isPending,
    nextPage,
    previousPage,
    total,
    page,
    setPage,
    toPage,
    isFetching,
  };
}
